import { BarChart3, LayoutDashboard, Plus, Package, Shield, ShieldCheck, UserCog, Database, Wallet, Receipt, AlertCircle, ChevronDown, ChevronRight, Settings, ClipboardList, Target, AlertTriangle, ShoppingCart, Calculator, LifeBuoy } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { UserRole } from '@/types';
import { useState, useEffect } from 'react';
import { cashSettlementApi } from '@/services/cashSettlementApi';

interface NavItem {
  name: string;
  icon: typeof LayoutDashboard;
  path: string;
  roles?: UserRole[];
  children?: NavItem[];
}

const getNavItems = (role: string): NavItem[] => {
  const baseItems: NavItem[] = [
    { name: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
  ];

  if (role === UserRole.SUPER_ADMIN || role === UserRole.ADMIN) {
    baseItems.push({ name: 'Branches', icon: Package, path: '/branches' });
    baseItems.push({ name: 'Roles', icon: Shield, path: '/roles' });
    baseItems.push({ name: 'Employees', icon: UserCog, path: '/employees' });
    baseItems.push({ name: 'Items', icon: Package, path: '/items' });
    baseItems.push({ name: 'Master Data', icon: Database, path: '/masters' });

    // Petty Cash Management for Admins
    baseItems.push({
      name: 'Petty Cash',
      icon: Wallet,
      path: '/petty-cash',
      children: [
        { name: 'Transfers', icon: Receipt, path: '/petty-cash/transfers' },
        { name: 'Branch Requests', icon: AlertCircle, path: '/petty-cash/requests' },
      ],
    });

    // Cash Settlement Management
    baseItems.push({ name: 'Cash Settlement', icon: Calculator, path: '/cash-settlements' });

    // Task Management for Admins
    baseItems.push({ name: 'Task Management', icon: ClipboardList, path: '/tasks' });

    // Customer Orders for Admins
    baseItems.push({ name: 'Customer Orders', icon: ShoppingCart, path: '/customer-orders' });

    // Warranty Management for Admins
    baseItems.push({ name: 'Warranties', icon: ShieldCheck, path: '/warranties' });

    // Branch Targets for Admins
    baseItems.push({ name: 'Branch Targets', icon: Target, path: '/branch-targets' });

    // Technicians
    baseItems.push({ name: 'Technicians', icon: UserCog, path: '/technicians' });

    // Technician Performance for Admins
    baseItems.push({ name: 'Tech Performance', icon: AlertTriangle, path: '/technician-performance' });

    // Reports
    baseItems.push({ name: 'Reports', icon: BarChart3, path: '/reports' });
  }

  // Support tickets — every role can raise one with screenshots / a recording
  baseItems.push({ name: 'Support', icon: LifeBuoy, path: '/support-tickets' });

  // Settings for Super Admin only
  if (role === UserRole.SUPER_ADMIN) {
    baseItems.push({ name: 'Settings', icon: Settings, path: '/settings' });
  }

  return baseItems;
};

interface SidebarProps {
  isOpen: boolean;
  onClose?: () => void;
}

export default function Sidebar({ isOpen, onClose }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const [expandedMenus, setExpandedMenus] = useState<string[]>(['Petty Cash']);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch pending settlement count for badge
  const { data: pendingCount } = useQuery({
    queryKey: ['settlement-pending-count-sidebar'],
    queryFn: () => cashSettlementApi.getPendingCount(),
    staleTime: 60 * 1000,
    refetchInterval: 2 * 60 * 1000,
    enabled: !!user && (user.role === UserRole.SUPER_ADMIN || user.role === UserRole.ADMIN),
  });
  const settlementBadge = pendingCount?.submittedCount || 0;

  if (!user) return null;

  const navItems = getNavItems(user.role);

  const toggleMenu = (name: string) => {
    setExpandedMenus((prev) =>
      prev.includes(name) ? prev.filter((item) => item !== name) : [...prev, name]
    );
  };

  const isChildActive = (children?: NavItem[]) => {
    if (!children) return false;
    return children.some((child) => child.path === location.pathname);
  };

  const renderNavItem = (item: NavItem) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedMenus.includes(item.name);
    const childActive = isChildActive(item.children);
    const Icon = item.icon;
    const isActive = location.pathname === item.path;

    if (hasChildren) {
      return (
        <div key={item.name} className="mb-2">
          <button
            onClick={() => toggleMenu(item.name)}
            className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg transition-all ${
              childActive
                ? 'bg-white/10 text-white'
                : 'text-white/70 hover:bg-white/5 hover:text-white'
            }`}
          >
            <div className="flex items-center gap-3">
              <Icon className="h-5 w-5 flex-shrink-0" />
              <span className="text-sm font-medium">{item.name}</span>
            </div>
            {isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
          {isExpanded && (
            <div className="ml-4 mt-1 space-y-1">
              {item.children?.map((child) => {
                const ChildIcon = child.icon;
                const isChildActive = location.pathname === child.path;
                return (
                  <button
                    key={child.path}
                    onClick={() => navigate(child.path)}
                    className={`w-full flex items-center gap-3 px-4 py-2 rounded-lg transition-all ${
                      isChildActive
                        ? 'bg-cyan-500 text-white shadow-lg'
                        : 'text-white/70 hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    <ChildIcon className="h-4 w-4" />
                    <span className="text-sm font-medium">{child.name}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    const badge = item.name === 'Cash Settlement' ? settlementBadge : 0;

    return (
      <button
        key={item.path}
        onClick={() => navigate(item.path)}
        className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg mb-2 transition-all ${
          isActive
            ? 'bg-white/10 text-white'
            : 'text-white/70 hover:bg-white/5 hover:text-white'
        }`}
      >
        <Icon className="h-5 w-5 flex-shrink-0" />
        <span className="text-sm font-medium">{item.name}</span>
        {badge > 0 && (
          <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold bg-red-500 text-white rounded-full">
            {badge}
          </span>
        )}
      </button>
    );
  };

  return (
    <>
      {/* Mobile backdrop */}
      {isMobile && isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={`fixed left-0 top-0 h-full flex flex-col bg-gradient-to-b from-[#1a0b2e] to-[#2d1b50] text-white transition-all duration-300 ${
          isOpen ? 'w-60' : 'w-0'
        } overflow-hidden z-50`}
      >
        {/* Logo */}
        <div className="p-6 flex items-center gap-3 flex-shrink-0">
          <img src="/logo-icon.png" alt="Track New" className="h-9 w-9 rounded-lg" />
          <span className="text-xl font-bold text-white tracking-tight">Track New</span>
        </div>

        {/* Navigation - scrollable */}
        <nav className="flex-1 overflow-y-auto px-3 pb-4 sidebar-scroll">
          {navItems.map((item) => renderNavItem(item))}
        </nav>
      </aside>
    </>
  );
}
